<template>
  <div class="padding">
    <h5 class="_300 m-b-md">FAQ Guidelines</h5>

    <div class="row">
      <div class="col-sm-4 col-xl-3">
        <div class="box">
          <div class="box-header b-b">
            <h3>Type</h3>
            <small>Filter by type</small>
          </div>
          <div class="box-body">
            <multiselect
              v-model="selectedType"
              :options="typeOptions"
              :show-labels="false"
              label="label"
              track-by="value"
              placeholder="All types"
              @input="resetAndFetch"
            />
          </div>
        </div>

        <div
          v-if="canManage"
          class="box"
        >
          <div class="box-header b-b">
            <h3>Status</h3>
            <small>Filter by status</small>
          </div>
          <div class="box-body">
            <multiselect
              v-model="selectedStatus"
              :options="statusOptions"
              :show-labels="false"
              label="label"
              track-by="value"
              placeholder="All status"
              @input="resetAndFetch"
            />
          </div>
        </div>

        <div class="box">
          <div class="box-header b-b">
            <h3>Category</h3>
            <small>Filter by category</small>
          </div>
          <div class="box-body">
            <multiselect
              v-model="selectedCategory"
              :options="categories"
              :show-labels="false"
              label="name"
              track-by="id"
              placeholder="All categories"
              @input="resetAndFetch"
            />
          </div>
        </div>
      </div>

      <div class="col-sm-8 col-xl-9">
        <div class="box">
          <div class="box-body">
            <div class="fg-toolbar">
              <div class="fg-toolbar-search-wrap">
                <input
                  v-model="query"
                  :class="{ 'is-invalid': queryError }"
                  class="form-control fg-toolbar-search"
                  type="text"
                  placeholder="Search by title or description..."
                  @input="debouncedFetch"
                >
                <small
                  v-if="queryError"
                  class="text-danger"
                >{{ queryError }}</small>
              </div>
              <button
                v-if="canManage"
                class="btn white fg-toolbar-create"
                @click="openCreate"
              >
                Create FAQ Guideline
              </button>
            </div>
          </div>
        </div>

        <div
          v-if="loading"
          class="box text-center fg-loading"
        >
          <div class="box-body text-muted">
            Loading...
          </div>
        </div>

        <faq-guideline-list
          v-else
          :items="faqGuidelines"
          :can-manage="canManage"
          @edit="openEdit"
          @detail="openDetail"
          @archive="archive"
          @delete="remove"
        />

        <footer class="dker p-a b-t">
          <div class="row">
            <div class="col text-center">
              <div class="inline VueTables__per-page">
                <div class="form-group form-inline VueTables__limit-field m-t-1 m-b-0">
                  <label>Records per page :</label>

                  <select
                    v-model="limit"
                    class="form-control"
                    name="limit"
                    @change="changeLimit"
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="40">40</option>
                    <option value="80">80</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col text-center">
              <div
                v-if="loaded"
                class="VuePagination justify-content-center m-t-1"
              >
                <pagination
                  ref="pagination"
                  :chunk="5"
                  :for="tableName"
                  :per-page="parseInt(limit)"
                  :records="faqGuidelineCount"
                  :vuex="false"
                  theme="bootstrap4"
                  @paginate="setPage"
                />
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>

    <faq-guideline-form
      v-if="canManage"
      ref="form"
      @saved="onSaved"
    />
    <faq-guideline-detail ref="detail" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Auth from '@js/plugins/auth';
import FaqGuidelineList from './faq-guideline-list.vue';
import FaqGuidelineForm from './faq-guideline-form.vue';
import FaqGuidelineDetail from './faq-guideline-detail.vue';

const STATUS_OPTIONS = [
    { label: 'Draft', value: 'draft' },
    { label: 'Published', value: 'published' },
    { label: 'Archived', value: 'archived' },
];

const TYPE_OPTIONS = [
    { label: 'FAQ', value: 'faq' },
    { label: 'Guideline', value: 'guideline' },
];

export default {
    components: {
        FaqGuidelineList,
        FaqGuidelineForm,
        FaqGuidelineDetail,
    },

    data() {
        return {
            canManage: Auth.hasPermissionTo('franchise_manage_faq_guideline'),
            statusOptions: STATUS_OPTIONS,
            typeOptions: TYPE_OPTIONS,
            selectedStatus: null,
            selectedType: null,
            selectedCategory: null,
            query: '',
            page: 1,
            limit: 10,
            loading: false,
            loaded: false,
            tableName: 'faq-guideline',
            debounceTimer: null,
        };
    },

    computed: {
        ...mapGetters('faqGuideline', ['faqGuidelines', 'faqGuidelineCount']),
        categories() {
            return this.$store.getters['faqGuideline/faqGuidelineCategories'];
        },
        queryError() {
            return this.htmlTagsSearchError(this.query);
        },
    },

    created() {
        this.$store.dispatch('faqGuideline/getFaqGuidelineCategories');
        this.fetchList();
    },

    methods: {
        async fetchList() {
            // Don't fire a request the server will reject — a 422 here would
            // land in the catch below and show a generic error popup.
            if (this.queryError) {
                return;
            }

            const params = {
                query: this.query || undefined,
                type: this.selectedType ? this.selectedType.value : undefined,
                category_id: this.selectedCategory
                    ? this.selectedCategory.id
                    : undefined,
                page: this.page,
                limit: this.limit,
            };

            // Status filter only applies to managers ("All status" ⇒ no
            // filter); dealers always see published regardless.
            if (this.canManage && this.selectedStatus) {
                params.status = this.selectedStatus.value;
            }

            this.loading = true;
            try {
                await this.$store.dispatch(
                    'faqGuideline/getFaqGuidelines',
                    params,
                );
            } catch (e) {
                this.swalError({ text: 'Unable to load FAQ Guidelines.' });
            } finally {
                this.loading = false;
                this.loaded = true;
            }
        },

        setPage(page) {
            this.page = page;
            this.fetchList();
        },

        changeLimit() {
            this.page = 1;
            if (this.$refs.pagination) {
                this.$refs.pagination.setPage(1);
            }
            this.fetchList();
        },

        resetAndFetch() {
            this.page = 1;
            if (this.$refs.pagination) {
                this.$refs.pagination.setPage(1);
            }
            this.fetchList();
        },

        debouncedFetch() {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => this.resetAndFetch(), 350);
        },

        openCreate() {
            this.$refs.form.show();
        },

        async openEdit(item) {
            try {
                const guideline = await this.$store.dispatch(
                    'faqGuideline/getFaqGuideline',
                    item.id,
                );
                this.$refs.form.show(guideline);
            } catch (e) {
                this.swalError({ text: 'Unable to load FAQ Guideline.' });
            }
        },

        async openDetail(item) {
            try {
                const guideline = await this.$store.dispatch(
                    'faqGuideline/getFaqGuideline',
                    item.id,
                );
                this.$refs.detail.show(guideline);
            } catch (e) {
                this.swalError({ text: 'Unable to load FAQ Guideline.' });
            }
        },

        archive(item) {
            this.swalAjaxConfirmation({
                title: 'Archive this FAQ Guideline?',
                text:
                    'Archived FAQ Guidelines are hidden from dealers but kept in the back office.',
                confirmText: 'Archive',
                preConfirm: () =>
                    this.$store.dispatch('faqGuideline/updateFaqGuideline', {
                        id: item.id,
                        payload: { status: 'archived' },
                    }),
                onSuccess: () => {
                    this.swal({ text: 'FAQ Guideline archived.' });
                    this.fetchList();
                },
            });
        },

        remove(item) {
            this.swalAjaxConfirmation({
                title: 'Delete this item?',
                text: 'This cannot be undone.',
                confirmText: 'Delete',
                preConfirm: () =>
                    this.$store.dispatch(
                        'faqGuideline/deleteFaqGuideline',
                        item.id,
                    ),
                onSuccess: () => {
                    this.swal({ text: 'FAQ Guideline deleted.' });
                    this.fetchList();
                },
            });
        },

        onSaved() {
            this.fetchList();
        },
    },
};
</script>

<style scoped>
.fg-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: nowrap;
    gap: 12px;
}
/* The wrapper carries the layout (it holds the input plus its validation
   message); the input just fills it. */
.fg-toolbar-search-wrap {
    flex: 0 0 auto;
    width: 260px;
    max-width: 60%;
}
.fg-toolbar-search {
    width: 100%;
}
.fg-toolbar-create {
    flex: 0 0 auto;
    white-space: nowrap;
}
.fg-loading {
    margin-bottom: 0;
}

@media (max-width: 575.98px) {
    .fg-toolbar {
        flex-direction: column;
        align-items: stretch;
    }
    .fg-toolbar-search-wrap {
        width: 100%;
        max-width: 100%;
    }
    .fg-toolbar-create {
        width: 100%;
    }
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/franchise_component/faq_guideline/faq-guideline-index.vue